import { Component } from '@theme/component';

export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;
  #animationTimer = undefined;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    clearTimeout(this.#animationTimer);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    const previousCurrent = this.#current;
    const previousIndex = this.#normalizeIndex(previousCurrent, slides.length);
    const relativeIndex = this.#normalizeIndex(current, slides.length);
    const direction = current >= previousCurrent ? 'up' : 'down';
    this.#current = current;

    clearTimeout(this.#animationTimer);
    slides.forEach((slide, index) => {
      slide.classList.remove(
        'announcement-bar__slide--leave-up',
        'announcement-bar__slide--enter-up',
        'announcement-bar__slide--leave-down',
        'announcement-bar__slide--enter-down'
      );
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    if (previousIndex === relativeIndex) return;

    const previousSlide = slides[previousIndex];
    const currentSlide = slides[relativeIndex];
    previousSlide?.classList.add(`announcement-bar__slide--leave-${direction}`);
    currentSlide?.classList.add(`announcement-bar__slide--enter-${direction}`);

    this.#animationTimer = setTimeout(() => {
      previousSlide?.classList.remove(`announcement-bar__slide--leave-${direction}`);
      currentSlide?.classList.remove(`announcement-bar__slide--enter-${direction}`);
    }, 600);
  }

  #normalizeIndex(index, length) {
    let relativeIndex = index % length;
    if (relativeIndex < 0) relativeIndex += length;
    return relativeIndex;
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
